// <title> y meta description por ruta (auditoría SEO 2026-10-23). PageHelmet los usa ANTES que
// lo que pase cada página: así el texto de buscadores se revisa en un solo sitio.
//
// Por qué existe: 16 páginas compartían la misma descripción por defecto, y en inglés, y varios
// títulos eran una palabra suelta («Pricing», «Trabajos», «Hablemos»). Cada texto sale del
// contenido real de su página (H1, subtítulo y secciones), sin prometer nada que no esté en ella.
//
// Reglas: el título va SIN la marca (PageHelmet añade « | InvokeApp» si no la lleva) y con un
// máximo de ~48 caracteres; la descripción entre 120 y 160.
//
// `h1`: el H1 de las páginas con la cabecera común (CommonPageHeader). Antes era una palabra
// suelta («Pricing», «Recursos», «Hablemos») que no decía qué es InvokeApp ni de qué va la
// página; la miga de pan sigue con el nombre corto.
const SEO_META = {
  // /ai-sales-follow-up NO va aquí a propósito: tiene su propio conmutador de idioma (inglés por
  // defecto, español con ?lang=es) independiente de la bandera del sitio, y el título y la
  // descripción salen de la página (src/pages/SalesFollowUp/salesFollowUpTexts.js).
  // Home: el H1 dice para quién es (antes «…con IA bajo tu marca», sin nombrar a las agencias).
  '/': {
    es: { h1: 'Atención al cliente, citas y seguimiento comercial para agencias y sus clientes' },
    en: { h1: 'Customer service, appointments and sales follow-up for agencies and their clients' },
  },
  '/get-started': {
    es: { h1: 'Cómo empezar con InvokeApp' },
    en: { h1: 'Get started with InvokeApp' },
  },
  '/about': {
    es: { h1: 'La plataforma de atención al cliente y CRM hecha para agencias' },
    en: { h1: 'The customer service and CRM platform built for agencies' },
  },
  '/services': {
    es: { title: 'Atención, citas y seguimiento para agencias', h1: 'Atención al cliente, citas y seguimiento comercial que tu agencia puede ofrecer' },
    en: { title: 'Customer service and follow-up for agencies', h1: 'Customer service, appointments and sales follow-up your agency can offer' },
  },
  '/platform': {
    es: { title: 'Plataforma InvokeApp para agencias', h1: 'La plataforma de atención al cliente, citas y CRM para agencias' },
    en: { title: 'The InvokeApp platform for agencies', h1: 'The customer service, appointments and CRM platform for agencies' },
  },
  '/contact': {
    es: { title: 'Contacto: habla con el equipo de InvokeApp', h1: 'Habla con el equipo de InvokeApp' },
    en: { title: 'Contact: talk to the InvokeApp team', h1: 'Talk to the InvokeApp team' },
  },
  '/pricing': {
    es: { title: 'Precios y planes de InvokeApp', h1: 'Precios y planes de InvokeApp' },
    en: { title: 'InvokeApp pricing and plans', h1: 'InvokeApp pricing and plans' },
  },
  '/faq': {
    es: {
      h1: 'Preguntas frecuentes sobre InvokeApp',
      title: 'Preguntas frecuentes sobre InvokeApp',
      description: 'Respuestas para agencias: cómo llevar la atención al cliente, las citas y el seguimiento comercial de tus clientes con playbooks, integraciones y flujos de activación.',
    },
    en: {
      h1: 'Frequently asked questions about InvokeApp',
      title: 'Frequently asked questions about InvokeApp',
      description: 'Answers for agencies: how to run customer service, appointments and sales follow-up for your clients with playbooks, integrations and activation flows.',
    },
  },
  // OCULTA 2026-10-28: la página /team está oculta (ruta comentada en App.js). Para volver a publicarla, descomentar.
  // '/team': {
  //   es: {
  //     h1: 'El equipo detrás de InvokeApp',
  //     title: 'Equipo de InvokeApp',
  //     description: 'Conoce al equipo fundador de InvokeApp, la plataforma de atención al cliente, citas, CRM y seguimiento comercial para agencias de marketing y sus clientes.',
  //   },
  //   en: {
  //     h1: 'The team behind InvokeApp',
  //     title: 'The InvokeApp team',
  //     description: 'Meet the founding team behind InvokeApp, the customer service, appointments, CRM and sales follow-up platform for marketing agencies and their clients.',
  //   },
  // },
  '/playbooks': {
    es: {
      h1: 'Playbooks listos para ofrecer a tus clientes',
      title: 'Playbooks para agencias: atención, citas y ventas',
      description: 'Playbooks listos para vender: agenda, pipeline comercial, contenido, recompra y atención 24/7. Calcula cuánto puedes facturar con cada uno.',
    },
    en: {
      h1: 'Ready-to-offer playbooks for your clients',
      title: 'Customer service and sales playbooks for agencies',
      description: 'Ready-to-sell playbooks: bookings, sales pipeline, content, repeat purchases and 24/7 support. Work out how much you can bill with each one.',
    },
  },
  '/documentation': {
    es: {
      h1: 'Centro de ayuda de InvokeApp',
      title: 'Centro de ayuda: manual de InvokeApp',
      description: 'Manual de InvokeApp por secciones: dashboard y KPIs, proyectos de cliente, flujos de trabajo, tokens de IA, catálogo de procesos y centro de activación.',
    },
    en: {
      h1: 'InvokeApp help center',
      title: 'Help center: the InvokeApp manual',
      description: 'The InvokeApp manual by section: dashboard and KPIs, client projects, workflows, AI tokens, the process catalog and the activation center.',
    },
  },
  '/demo': {
    es: {
      h1: 'Demo en vivo de InvokeApp',
      title: 'Demo de InvokeApp: la plataforma por dentro',
      description: 'Recorre una agencia de ejemplo en InvokeApp, con sus proyectos, procesos, clientes y métricas, y reserva una demo en directo con el equipo.',
    },
    en: {
      h1: 'InvokeApp live demo',
      title: 'InvokeApp demo: the platform from the inside',
      description: 'Walk through a sample agency in InvokeApp, with its projects, processes, clients and metrics, and book a live demo with the team.',
    },
  },
  '/changelog': {
    es: {
      h1: 'Novedades de InvokeApp',
      title: 'Changelog: novedades de InvokeApp',
      description: 'Actualizaciones del producto para agencias que usan InvokeApp: las funciones, integraciones y mejoras que trae cada versión de la plataforma.',
    },
    en: {
      h1: 'What is new in InvokeApp',
      title: 'Changelog: what is new in InvokeApp',
      description: 'Product updates for agencies using InvokeApp: the features, integrations and improvements that each version of the platform brings.',
    },
  },
  '/agencies': {
    es: {
      title: 'Atención, citas y CRM para tus clientes',
      description: 'Plataforma para agencias que quieren ofrecer atención al cliente, citas, CRM y seguimiento comercial a sus clientes sin contratar desarrolladores.',
    },
    en: {
      title: 'Service, appointments and CRM for your clients',
      description: 'A platform for agencies that want to offer customer service, appointments, CRM and sales follow-up to their clients without hiring developers.',
    },
  },
  '/partners': {
    es: {
      title: 'Programa de partners de InvokeApp',
      description: 'Programa de partnership B2B: InvokeApp pone la infraestructura tecnológica y tú la estrategia y la implementación para vender automatización.',
    },
    en: {
      title: 'The InvokeApp partner program',
      description: 'A B2B partnership program: InvokeApp provides the technology infrastructure and you bring the strategy and implementation to sell automation.',
    },
  },
  '/product': {
    es: {
      title: 'Infraestructura de automatización e IA',
      description: 'Plataforma para equipos técnicos y agencias que quieren construir sus automatizaciones e IA como infraestructura robusta, escalable y reutilizable.',
    },
    en: {
      title: 'Automation and AI infrastructure',
      description: 'A platform for technical teams and agencies that want to build their automations and AI as robust, scalable and reusable infrastructure.',
    },
  },
  '/scale': {
    es: {
      title: 'Escala tus servicios de automatización e IA',
      description: 'Infraestructura para agencias y estudios que convierten proyectos de automatización aislados en sistemas replicables y rentables.',
    },
    en: {
      title: 'Scale your automation and AI services',
      description: 'Infrastructure for agencies and studios that turn one-off automation projects into repeatable, profitable systems.',
    },
  },
  '/affiliates': {
    es: {
      title: 'Programa de afiliados: desde el 25% por venta',
      description: 'Únete al programa de afiliados de InvokeApp y gana desde el 25% de comisión por cada cliente que refieras, y hasta el 35% según tu nivel.',
    },
    en: {
      title: 'Affiliate program: from 25% per sale',
      description: 'Join the InvokeApp affiliate program and earn from 25% commission on every client you refer, and up to 35% depending on your tier.',
    },
  },
  '/affiliates/guide': {
    es: {
      title: 'Guía para afiliados de InvokeApp',
      description: 'Cómo promocionar InvokeApp como afiliado: tu enlace, el seguimiento de tus resultados, recursos listos para usar y las últimas novedades.',
    },
    en: {
      title: 'The InvokeApp affiliate guide',
      description: 'How to promote InvokeApp as an affiliate: your link, tracking your results, ready-to-use resources and the latest product updates.',
    },
  },
  '/roadmap': {
    es: {
      h1: 'Roadmap de InvokeApp',
      title: 'Roadmap de InvokeApp: vota funcionalidades',
      description: 'Vota las próximas funcionalidades de InvokeApp y ayuda a definir el roadmap de la plataforma: las propuestas más votadas se priorizan.',
    },
    en: {
      h1: 'InvokeApp roadmap',
      title: 'InvokeApp roadmap: vote on features',
      description: 'Vote on the next InvokeApp features and help shape the platform roadmap: the most voted proposals get prioritised.',
    },
  },
  '/how-it-works': {
    es: {
      h1: 'Cómo funciona InvokeApp',
      title: 'Cómo funciona InvokeApp, paso a paso',
      description: 'De cero a operativo en cinco pasos: descubre, conecta tus herramientas, configura, lanza y escala los servicios que ofreces a tus clientes con InvokeApp.',
    },
    en: {
      h1: 'How InvokeApp works',
      title: 'How InvokeApp works, step by step',
      description: 'From zero to live in five steps: discover, connect your tools, configure, launch and scale the services you offer your clients with InvokeApp.',
    },
  },
  '/product-tour': {
    es: {
      h1: 'Tour por la plataforma InvokeApp',
      title: 'Tour de la plataforma InvokeApp',
      description: 'Tour guiado por InvokeApp: dashboard, portal del cliente, motor de automatización, playbooks y ROI, analíticas e informes e integraciones.',
    },
    en: {
      h1: 'A tour of the InvokeApp platform',
      title: 'A tour of the InvokeApp platform',
      description: 'A guided tour of InvokeApp: dashboard, client portal, automation engine, playbooks and ROI, analytics and reports, and integrations.',
    },
  },
  '/customer-portal': {
    es: {
      title: 'Portal del cliente',
      description: 'Accede a tu panel de InvokeApp, a tus facturas y al soporte.',
    },
    en: {
      title: 'Customer portal',
      description: 'Access your InvokeApp dashboard, your invoices and support.',
    },
  },
  '/jobs': {
    es: {
      h1: 'Trabaja en InvokeApp',
      title: 'Trabajar en InvokeApp',
      description: 'Ofertas de empleo de InvokeApp. Ahora mismo no hay vacantes abiertas, pero puedes enviarnos tu CV con una candidatura espontánea.',
    },
    en: {
      h1: 'Work at InvokeApp',
      title: 'Work at InvokeApp',
      description: 'Job openings at InvokeApp. There are no open positions right now, but you can send us your CV as a speculative application.',
    },
  },
  '/press': {
    es: {
      h1: 'Kit de prensa de InvokeApp',
      title: 'Kit de prensa y recursos de marca',
      description: 'Logos de InvokeApp en SVG y PNG, colores de marca y contacto de prensa para medios, partners y eventos.',
    },
    en: {
      h1: 'InvokeApp press kit',
      title: 'Press kit and brand assets',
      description: 'InvokeApp logos in SVG and PNG, brand colours and a press contact for media, partners and events.',
    },
  },
  '/resources': {
    es: {
      h1: 'Recursos para vender servicios de IA',
      title: 'Recursos para vender servicios de IA',
      description: 'Kits, webinars y artículos para crear y vender servicios de IA: el Kit de IA, AI Commercial System, la comunidad y el blog.',
    },
    en: {
      h1: 'Resources for selling AI services',
      title: 'Resources for selling AI services',
      description: 'Kits, webinars and articles to build and sell AI services: the AI Kit, the AI Commercial System, the community and the blog.',
    },
  },
  // Histórico legal (cláusula 14.3 de las condiciones). Cada versión, /legal/history/<doc>/<v>,
  // lleva su propio título y noindex desde LegalHistoryVersion.jsx.
  '/legal/history': {
    es: {
      h1: 'Versiones de los textos legales de InvokeApp',
      title: 'Versiones de los textos legales',
      description: 'La versión vigente y las anteriores de las condiciones, el acuerdo de encargo, la privacidad, los subencargados y las cookies de InvokeApp, con su fecha.',
    },
    en: {
      h1: 'Versions of the InvokeApp legal documents',
      title: 'Legal document versions',
      description: 'The current and previous versions of the InvokeApp terms of service, data processing agreement, privacy policy, sub-processors and cookie policy, with dates.',
    },
  },
};

export function seoMetaFor(pathname, lang) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  const entry = SEO_META[path];
  return (entry && (entry[lang] || entry.es)) || {};
}

export default SEO_META;
